/**
 * OptionsEditor — edits a list of numeric {label,value} scale options at a Formik
 * path (VER-109). Reused for the survey's shared default scale and for a
 * multi-choice question's own options.
 */
import React from "react";
import { Box, Button, Grid, IconButton, TextField, Tooltip } from "@mui/material";
import { AddOutlined, DeleteOutline } from "@mui/icons-material";
import { FieldArray, useFormikContext } from "formik";
import type { ScaleOption, SurveyDefinition } from "./survey.types";
import { useFieldError } from "./useFieldError";

export type OptionsEditorProps = {
    /** Formik path to the ScaleOption[] (e.g. "defaultScale.options"). */
    name: string;
    /** Minimum number of options that must remain (delete disabled at the floor). */
    minOptions?: number;
    addLabel?: string;
};

export const OptionsEditor = ({ name, minOptions = 2, addLabel = "Add option" }: OptionsEditorProps): JSX.Element => {
    const { handleChange, handleBlur, getFieldProps } = useFormikContext<SurveyDefinition>();
    const fieldError = useFieldError();

    // `values` is the whole survey; read the array at `name` via getFieldProps.
    const options = (getFieldProps(name).value as ScaleOption[] | undefined) ?? [];

    return (
        <FieldArray name={name}>
            {({ push, remove }) => (
                <Box>
                    <Grid container spacing={2}>
                        {options.map((option, index) => (
                            <Grid item xs={12} key={index} container spacing={2} alignItems="center">
                                <Grid item xs={7} sm={8}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name={`${name}.${index}.label`}
                                        label={`Option ${index + 1} label`}
                                        value={option.label}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(fieldError(`${name}.${index}.label`))}
                                        helperText={fieldError(`${name}.${index}.label`)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        name={`${name}.${index}.value`}
                                        label="Value"
                                        value={option.value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(fieldError(`${name}.${index}.value`))}
                                        helperText={fieldError(`${name}.${index}.value`)}
                                    />
                                </Grid>
                                <Grid item xs={2} sm={1}>
                                    <Tooltip title="Remove option">
                                        <span>
                                            <IconButton
                                                aria-label={`Remove option ${index + 1}`}
                                                onClick={() => remove(index)}
                                                disabled={options.length <= minOptions}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                    <Button
                        sx={{ mt: 2 }}
                        variant="outlined"
                        size="small"
                        startIcon={<AddOutlined />}
                        onClick={() => push({ label: "", value: options.length + 1 })}
                    >
                        {addLabel}
                    </Button>
                </Box>
            )}
        </FieldArray>
    );
};
